import React from 'react'

import { LeftImageContent, NoImageContent, RightImageContent, TopImageContent } from '..'
import { SectionProps } from '../../../../core/types/Help'
import { List, SectionTitle, Text } from '../ContentItem'

import { HELP_IMAGES } from '../../../constants/HelpImages'
import { buildDynamicCopy } from '../../../../core/utils/Localization'

const Accounts: React.FC<SectionProps> = ({ copy, topicKey }) => {
  const contentMap = {
    'accounts-overview': (
      <React.Fragment>
        <RightImageContent
          altImage={copy.accounts_overview.alt_image}
          content={
            <React.Fragment>
              <Text content={copy.accounts_overview.description} sx={{ mt: 24 }} />
              <List
                items={[
                  copy.accounts_overview.widget_overview_instruction_step_1,
                  copy.accounts_overview.widget_overview_instruction_step_2,
                ]}
              />
              <Text content={copy.accounts_overview.account_view_text} sx={{ mt: 12 }} />
            </React.Fragment>
          }
          image={HELP_IMAGES.ACCOUNT_OVERVIEW}
          imageLargeBreakPoint={5}
        />
        <SectionTitle content={copy.accounts_overview.edit_account_title} sx={{ pt: 48 }} />
        <LeftImageContent
          altImage={copy.accounts_overview.alt_image_account_details}
          content={
            <React.Fragment>
              <List
                items={[
                  buildDynamicCopy(
                    copy.accounts_overview.edit_account_step_1_instruction,
                    <b>{copy.accounts_overview.edit_account_step_1_bold}</b>,
                  ) as React.ReactNode,
                  copy.accounts_overview.edit_account_step_2_instruction,
                  buildDynamicCopy(
                    copy.accounts_overview.edit_account_step_3_instruction,
                    <b>{copy.accounts_overview.edit_account_step_3_bold_1}</b>,
                    <b>{copy.accounts_overview.edit_account_step_3_bold_2}</b>,
                    <b>{copy.accounts_overview.edit_account_step_3_bold_3}</b>,
                  ) as React.ReactNode,
                ]}
                listType="ol"
                subItemPlacement={1}
                subItems={[
                  copy.accounts_overview.edit_account_step_2_details_account_name,
                  copy.accounts_overview.edit_account_step_2_details_interest_rate,
                  copy.accounts_overview.edit_account_step_2_details_credit_limit,
                  copy.accounts_overview.edit_account_step_2_details_account_balance,
                ]}
              />
            </React.Fragment>
          }
          image={HELP_IMAGES.ACCOUNT_DETAILS}
          imageLargeBreakPoint={3}
        />
        <SectionTitle content={copy.accounts_overview.add_account_title} sx={{ pt: 48 }} />
        <TopImageContent
          altImage={copy.accounts_overview.alt_image_manage_connections}
          content={
            <React.Fragment>
              <List
                items={[
                  buildDynamicCopy(
                    copy.accounts_overview.add_account_step_1_instruction,
                    <b>{copy.accounts_overview.add_account_step_1_bold}</b>,
                  ) as React.ReactNode,
                  buildDynamicCopy(
                    copy.accounts_overview.add_account_step_2_instruction,
                    <b>{copy.accounts_overview.add_account_step_2_bold}</b>,
                  ) as React.ReactNode,
                  buildDynamicCopy(
                    copy.accounts_overview.add_account_step_3_instruction,
                    <b>{copy.accounts_overview.add_account_step_3_bold}</b>,
                  ) as React.ReactNode,
                  copy.accounts_overview.add_account_step_4_instruction,
                  copy.accounts_overview.add_account_step_5_instruction,
                ]}
                listType="ol"
              />
              <Text
                content={
                  buildDynamicCopy(
                    copy.accounts_overview.exclude_account_info_text,
                    <b>{copy.accounts_overview.exclude_account_info_bold_1}</b>,
                    <b>{copy.accounts_overview.exclude_account_info_bold_2}</b>,
                    <b>{copy.accounts_overview.exclude_account_info_bold_3}</b>,
                    <b>{copy.accounts_overview.exclude_account_info_bold_4}</b>,
                  ) as React.ReactNode
                }
              />
              <SectionTitle content={copy.accounts_overview.hide_account_title} />
              <List
                items={[
                  buildDynamicCopy(
                    copy.accounts_overview.hide_account_step_1_instruction,
                    <b>{copy.accounts_overview.hide_account_step_1_bold}</b>,
                  ) as React.ReactNode,
                  buildDynamicCopy(
                    copy.accounts_overview.hide_account_step_2_instruction,
                    <b>{copy.accounts_overview.hide_account_step_2_bold}</b>,
                  ) as React.ReactNode,
                ]}
                listType="ol"
              />
              <SectionTitle content={copy.accounts_overview.unhide_account_title} />
              <List
                items={[
                  buildDynamicCopy(
                    copy.accounts_overview.unhide_account_step_1_instruction,
                    <b>{copy.accounts_overview.unhide_account_step_1_bold}</b>,
                  ) as React.ReactNode,
                  buildDynamicCopy(
                    copy.accounts_overview.unhide_account_step_2_instruction,
                    <b>{copy.accounts_overview.unhide_account_step_2_bold}</b>,
                  ) as React.ReactNode,
                ]}
                listType="ol"
              />
            </React.Fragment>
          }
          hasDivider={false}
          image={HELP_IMAGES.ACCOUNT_MANAGE_CONNECTIONS}
          imageLargeBreakPoint={10}
        />
      </React.Fragment>
    ),
    'trouble-adding-an-account': (
      <NoImageContent
        content={
          <React.Fragment>
            <Text content={copy.trouble_adding_an_account.steps_title} />
            <List
              items={[
                copy.trouble_adding_an_account.step_1,
                copy.trouble_adding_an_account.step_2,
                copy.trouble_adding_an_account.step_3,
                copy.trouble_adding_an_account.step_4,
                copy.trouble_adding_an_account.step_5,
                copy.trouble_adding_an_account.step_6,
                copy.trouble_adding_an_account.step_7,
                copy.trouble_adding_an_account.step_8,
              ]}
            />
            <Text content={copy.trouble_adding_an_account.support_text} sx={{ mt: 12 }} />
          </React.Fragment>
        }
        hasDivider={false}
        sx={{ mt: 24 }}
      />
    ),
    'need-to-fix-a-broken-account': (
      <LeftImageContent
        altImage={copy.need_to_fix_a_broken_account.alt_image}
        content={
          <React.Fragment>
            <Text content={copy.need_to_fix_a_broken_account.description_1} sx={{ mt: 24 }} />
            <Text content={copy.need_to_fix_a_broken_account.description_2} sx={{ mt: 12 }} />
            <Text
              content={copy.need_to_fix_a_broken_account.issues_persist_title}
              sx={{ mt: 12 }}
            />
            <List
              items={[
                copy.need_to_fix_a_broken_account.step_1,
                buildDynamicCopy(
                  copy.need_to_fix_a_broken_account.step_2,
                  <b>{copy.need_to_fix_a_broken_account.step_2_bold}</b>,
                ) as React.ReactNode,
                copy.need_to_fix_a_broken_account.step_3,
              ]}
            />
          </React.Fragment>
        }
        hasDivider={false}
        image={HELP_IMAGES.ACCOUNT_BROKEN_CONNECTION}
        imageLargeBreakPoint={3}
        sx={{ pt: 24 }}
      />
    ),
    'seeing-outdated-accounts-or-paid-off-loans': (
      <NoImageContent
        content={<Text content={copy.seeing_outdated_accounts_or_paid_off_loans.description} />}
        hasDivider={false}
        sx={{ pt: 8 }}
      />
    ),
    'seeing-duplicate-accounts': (
      <NoImageContent
        content={
          <React.Fragment>
            <SectionTitle content={copy.seeing_duplicate_accounts.warning_title} />
            <SectionTitle content={copy.seeing_duplicate_accounts.instructions_title} />
            <List
              items={[
                copy.seeing_duplicate_accounts.step_1_instruction,
                buildDynamicCopy(
                  copy.seeing_duplicate_accounts.step_2_instruction,
                  <b>{copy.seeing_duplicate_accounts.step_2_bold}</b>,
                  <b>{copy.seeing_duplicate_accounts.step_2_bold_2}</b>,
                ) as React.ReactNode,
                copy.seeing_duplicate_accounts.step_3_instruction,
                buildDynamicCopy(
                  copy.seeing_duplicate_accounts.step_4_instruction,
                  <b>{copy.seeing_duplicate_accounts.step_4_bold}</b>,
                ) as React.ReactNode,
              ]}
              listType="ol"
            />
          </React.Fragment>
        }
        hasDivider={false}
      />
    ),
  }

  return contentMap[topicKey as keyof typeof contentMap]
}

export default Accounts
